/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { Box, Dialog, Divider, IconButton, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectWhatsNewMarkdownContent } from '../../../store';
import { parseWhatsNewMarkdown } from '../../../utils/WhatsNewMarkdownParser';
import Scrollbars from '../layout/Scrollbars';
import appTheme from '../theme/appTheme.json';
import WhatsNewReleaseSection from './WhatsNewReleaseSection';

export const WHATS_NEW_DISCLAIMER = 'Not all features may be available for all customers depending on contract terms';

export interface WhatsNewPanelProps {
  open: boolean;
  onClose: () => void;
}

const WhatsNewPanel: React.FC<WhatsNewPanelProps> = ({ open, onClose }) => {
  const markdownContent = useSelector(selectWhatsNewMarkdownContent);
  const releases = useMemo(() => parseWhatsNewMarkdown(markdownContent).releases, [markdownContent]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: 'calc(100% - 24px)', sm: '640px' },
          maxWidth: 'calc(100% - 24px)',
          height: { xs: 'calc(100% - 24px)', sm: 'min(860px, calc(100% - 245px))' },
          backgroundColor: appTheme.darkGrayTextColor,
          color: appTheme.primaryTextColor,
          border: `1px solid ${appTheme.mptGray}`,
          borderRadius: '8px',
          boxShadow: '0px 10px 30px 2px rgba(0, 0, 0, 0.10)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            padding: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            backgroundColor: appTheme.mptPrimaryBackgroundColor,
          }}
        >
          <Box sx={{ display: 'flex', flex: '1 0 0', alignItems: 'flex-start', gap: '8px', minWidth: 0 }}>
            <NewReleasesIcon sx={{ fontSize: '24px', color: appTheme.primaryTextColor, marginTop: '2px' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography
                sx={{
                  color: appTheme.primaryTextColor,
                  fontFamily: appTheme.viasatFontFamilyHeavy,
                  fontSize: '20px',
                  fontWeight: 800,
                  lineHeight: '28px',
                  letterSpacing: '0.2px',
                }}
              >
                What&apos;s new
              </Typography>
              <Typography
                sx={{
                  color: appTheme.secondaryTextColor,
                  fontFamily: appTheme.appFontFamily,
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                }}
              >
                {WHATS_NEW_DISCLAIMER}
              </Typography>
            </Box>
          </Box>

          <IconButton
            aria-label="Close what's new panel"
            onClick={onClose}
            sx={{
              color: appTheme.primaryTextColor,
              padding: 0,
              width: '24px',
              height: '24px',
              marginTop: '2px',
              flexShrink: 0,
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: '24px' }} />
          </IconButton>
        </Box>
        <Divider sx={{ borderTopColor: appTheme.mptGray }} />

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Scrollbars>
            <Box sx={{ padding: '20px 24px 24px' }}>
              {releases.length > 0 ? (
                releases.map((release, index: number) => (
                  <WhatsNewReleaseSection key={`${release.releaseDate}-${index}`} release={release} />
                ))
              ) : (
                <Typography
                  sx={{
                    color: appTheme.grayTextColor,
                    fontFamily: appTheme.appFontFamily,
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                  }}
                >
                  No release notes are available right now.
                </Typography>
              )}
            </Box>
          </Scrollbars>
        </Box>
      </Box>
    </Dialog>
  );
};

export default WhatsNewPanel;
